import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal", "modalTitle", "modalContent"];

  connect() {
    this.modal = document.getElementById("entity-modal");
    this.modalTitle = document.getElementById("entity-modal-title");
    this.modalContent = document.getElementById("entity-modal-content");
  }

  showAll(event) {
    event.preventDefault();
    const isSignedIn = this.getMetaTag("user-signed-in") === "true";
    const entityType = event.currentTarget.dataset.entityType;
    const domainSlug = this.getMetaTag("domain-slug");

    if (!isSignedIn) {
      const signupModal = document.getElementById("signup-modal");
      if (signupModal) signupModal.showModal();
      return;
    }

    const analysisJobId = this.getMetaTag("analysis-job-id");
    window.location.href = `/domains/${domainSlug}/entities?type=${entityType}&analysis_job_id=${analysisJobId}`;
  }

  async fetchEntities(domainId, entityType) {
    try {
      const domainSlug = this.getMetaTag("domain-slug");
      const response = await fetch(
        `/domains/${domainSlug}/entities?type=${entityType}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      this.renderEntitiesTable(data, entityType);
    } catch (error) {
      console.error("Error fetching entities:", error);
      this.modalContent.innerHTML = `
        <div class="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span>Error loading entities. Please try again.</span>
        </div>
      `;
    }
  }

  renderEntitiesTable(entities, entityType) {
    if (!entities || entities.length === 0) {
      this.modalContent.innerHTML = `
        <div class="alert">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-info shrink-0 w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          <span>No entities found.</span>
        </div>
      `;
      return;
    }

    // Group entities by name and calculate total occurrences and average prominence
    const groupedEntities = this.groupEntitiesByName(entities);

    // Calculate total occurrences for percentage calculation
    const totalOccurrences = groupedEntities.reduce(
      (sum, entity) => sum + entity.occurrences,
      0
    );

    // Create table HTML
    let tableHTML = `
      <table class="table table-zebra">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Name</th>
            <th>Occurrences</th>
            <th>Percentage</th>
          </tr>
        </thead>
        <tbody>
    `;

    // Add table rows
    groupedEntities.forEach((entity, index) => {
      // Calculate percentage
      const percentage =
        totalOccurrences > 0
          ? ((entity.occurrences / totalOccurrences) * 100).toFixed(1)
          : 0;

      tableHTML += `
        <tr>
          <td>${index + 1}</td>
          <td class="max-w-md truncate">
            ${this.formatEntityName(entity.name, entityType)}
          </td>
          <td>${entity.occurrences}</td>
          <td>${percentage}%</td>
        </tr>
      `;
    });

    tableHTML += `
        </tbody>
      </table>
    `;

    this.modalContent.innerHTML = tableHTML;
  }

  groupEntitiesByName(entities) {
    // Group entities by name (case-insensitive)
    const groupedByName = {};

    entities.forEach((entity) => {
      const lowerName = entity.name.toLowerCase();
      if (!groupedByName[lowerName]) {
        groupedByName[lowerName] = {
          id: entity.id,
          name: entity.name,
          occurrences: 0,
          prominence: 0,
          totalWeightedProminence: 0,
          entities: [],
        };
      }

      groupedByName[lowerName].occurrences += entity.occurrences;
      groupedByName[lowerName].totalWeightedProminence +=
        entity.prominence * entity.occurrences;
      groupedByName[lowerName].entities.push(entity);
    });

    // Calculate average prominence and convert to array
    const result = Object.values(groupedByName).map((group) => {
      group.prominence = group.totalWeightedProminence / group.occurrences;
      delete group.totalWeightedProminence;
      return group;
    });

    // Sort by occurrences (descending)
    return result.sort((a, b) => b.occurrences - a.occurrences);
  }

  formatEntityName(name, entityType) {
    if (entityType === "WEBSITE_DOMAIN" || entityType === "PAGE_URL") {
      return `<div class="flex items-center gap-2">
                <img src="https://www.google.com/s2/favicons?domain=${name}" class="w-4 h-4">
                <span>${name}</span>
              </div>`;
    }
    return name;
  }

  getEntityDisplayName(entityType) {
    // Map entity types to display names
    const displayNames = {
      WEBSITE_DOMAIN: "Websites",
      PAGE_URL: "Pages",
      PRODUCT: "Products",
      ORGANIZATION: "Organizations",
      PERSON: "People",
      LOCATION: "Locations",
      TOPIC: "Topics",
    };

    return (
      displayNames[entityType] || entityType.replace(/_/g, " ").toLowerCase()
    );
  }

  getMetaTag(name) {
    const metaTag = document.querySelector(`meta[name="${name}"]`);
    return metaTag ? metaTag.getAttribute("content") : null;
  }
}
